'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import type { FilterState } from './ProductsClient';
import CampaignCard, { type Campaign } from './CampaignCard';

interface CampaignGridProps {
  filters: FilterState;
  searchQuery: string;
  onApplyClick: (campaign: {
    id: string;
    brand: string;
    product: string;
    category: string;
    reward_amount: number;
  }) => void;
}

export default function CampaignGrid({ filters, searchQuery, onApplyClick }: CampaignGridProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCampaigns() {
      const supabase = createClient();
      setLoading(true);
      try {
        let query = supabase
          .from('campaigns')
          .select('id, brand, product, category, image_url, reward_amount, avg_rating, available_slots, marketplace_name, filled_slots, total_slots')
          .eq('campaign_status', 'active')
          .gte('reward_amount', filters.minReward)
          .lte('reward_amount', filters.maxReward);

        if (filters.category !== 'All') {
          query = query.eq('category', filters.category);
        }
        if (filters.marketplace !== 'All') {
          query = query.eq('marketplace_name', filters.marketplace);
        }
        if (filters.minRating > 0) {
          query = query.gte('avg_rating', filters.minRating);
        }
        if (filters.brand.trim()) {
          query = query.ilike('brand', `%${filters.brand.trim()}%`);
        }

        const { data, error } = await query.order('avg_rating', { ascending: false });

        if (error) {
          console.log('Campaigns fetch error:', error.message);
        } else {
          setCampaigns(data || []);
        }
      } catch (err) {
        console.log('Campaigns fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchCampaigns();
  }, [filters]);

  // Client-side full-text search across product, brand, category
  const filtered = searchQuery.trim()
    ? campaigns.filter((c) => {
        const q = searchQuery.toLowerCase();
        return (
          c.product?.toLowerCase().includes(q) ||
          c.brand?.toLowerCase().includes(q) ||
          c.category?.toLowerCase().includes(q)
        );
      })
    : campaigns;

  if (loading) {
    return (
      <div>
        <div className="text-sm text-muted-foreground mb-4">Loading campaigns...</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden animate-pulse">
              <div className="h-44 bg-muted" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-8 bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Icon name="MagnifyingGlassIcon" size={48} className="text-border mb-4" />
        <h3 className="font-heading font-semibold text-foreground mb-2">No campaigns found</h3>
        <p className="text-muted-foreground text-sm">
          {searchQuery ? `No results for "${searchQuery}" — try different keywords or clear filters` : 'Try adjusting your filters'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">
          {filtered.length} campaign{filtered.length !== 1 ? 's' : ''} found
          {searchQuery && <span className="ml-1">for <strong className="text-foreground">"{searchQuery}"</strong></span>}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((campaign) => (
          <CampaignCard key={campaign.id} campaign={campaign} onApplyClick={onApplyClick} />
        ))}
      </div>
    </div>
  );
}
